import PropTypes from 'prop-types'
import { isNil } from 'ramda'
import BasicObject from 'model/basicObject'
import TicketTypeGroup from 'model/tickets/ticketType/ticketTypeGroup'
import {
  Ticket_Type_Code,
  PBM_CCDP_TYPES,
  PBM_CCWMP_TYPES,
  PBM_FINANCIAL_TYPES,
  BILLING_ADVOCACY_TYPES,
  PBM_CONTACT_CENTER_TYPES,
  PBM_CONTACT_CENTER_COMMON_TYPES
} from 'constants/subjectTypeConstants'

// Nearly identical to TicketState
class TicketType extends BasicObject {
  constructor(init) {
    super(init)
    // console.log('> TicketType constructor -', init)
    if (isNil(init)) return undefined
    this._active = init.active
    this._code = init.code
    this._display_name = init.display_name
    this._description = init.description
    this._usage = init.usage
    this._enable_surveys = init.enable_surveys
    this._for_bulk_create = init.for_bulk_create
    this._survey_id = init.survey_id
    this._follow_up_ticket_type_id = init.follow_up_ticket_type_id
    this._group = new TicketTypeGroup(init.group)
  }

  static shape = {
    id: PropTypes.string.isRequired,
    active: PropTypes.bool,
    code: PropTypes.string,
    display_name: PropTypes.string,
    description: PropTypes.string,
    usage: PropTypes.string,
    enable_surveys: PropTypes.bool,
    for_bulk_create: PropTypes.bool,
    survey_id: PropTypes.string,
    follow_up_ticket_type_id: PropTypes.string,
    group: PropTypes.instanceOf(TicketTypeGroup)
  }

  get active() {
    return this._active
  }

  get code() {
    return this._code
  }

  get display_name() {
    return this._display_name
  }

  get description() {
    return this._description
  }

  get usage() {
    return this._usage
  }

  get enable_surveys() {
    return this._enable_surveys
  }

  get for_bulk_create() {
    return this._for_bulk_create
  }

  get survey_id() {
    return this._survey_id
  }

  get follow_up_ticket_type_id() {
    return this._follow_up_ticket_type_id
  }

  get group() {
    return this._group
  }

  // Calculable
  get isAccumulatorIssue() {
    return this._code === Ticket_Type_Code.PbmDoAccumulatorIssue
  }

  get isAppointment() {
    return this._code === Ticket_Type_Code.Appointment
  }

  get isBilling() {
    return BILLING_ADVOCACY_TYPES.includes(this._code)
  }

  get isBenefitInquiry() {
    return this._code === Ticket_Type_Code.PbmCboBenefitQuestion
  }

  get isDiabetesProgram() {
    return PBM_CCDP_TYPES.includes(this._code)
  }

  get isDrugListUpdate() {
    return this._code === Ticket_Type_Code.PbmCboDrugListUpdate
  }

  get isWeightLossProgram() {
    return PBM_CCWMP_TYPES.includes(this._code)
  }

  get isOverride() {
    return this._code === Ticket_Type_Code.Override
  }

  get isPriorAuthorization() {
    return this._code === Ticket_Type_Code.PriorAuthorization
  }

  get isFinancialProgram() {
    return PBM_FINANCIAL_TYPES.includes(this._code)
  }

  get isSpecialtyCostPlusConversion() {
    return this._code === Ticket_Type_Code.SpecialtyCostPlusConversion
  }

  get isPbmContactCenter() {
    return PBM_CONTACT_CENTER_TYPES.includes(this._code)
  }

  get isPbmContactCenterCommon() {
    return PBM_CONTACT_CENTER_COMMON_TYPES.includes(this._code)
  }

  get isPriorAuthorizationStatus() {
    return this._code === Ticket_Type_Code.PbmCcoPriorAuthorizationStatus
  }

  get isFindCare() {
    return this._code === Ticket_Type_Code.FindCare
  }

  get isMailOrder() {
    return this._code === Ticket_Type_Code.PbmMailOrderTransfer
  }

  get isDrugSearch() {
    return this._code === Ticket_Type_Code.PbmProactiveDrugSearch
  }

  get isPharmacyId() {
    return this._code === Ticket_Type_Code.CpPharmacyIdRequest
  }

  get isEligibility() {
    return this._code === Ticket_Type_Code.CpEligibilityRequest
  }

  get isGeneralInquiry() {
    return this._code === Ticket_Type_Code.CpGeneralInquiry
  }

  get isSpecialtyRequest() {
    return this._code === Ticket_Type_Code.CpSpecialtyRequest
  }

  get isPriorAuthRequest() {
    return this._code === Ticket_Type_Code.CpPriorAuthRequest
  }

  get isMailOrderRequest() {
    return this._code === Ticket_Type_Code.CpMailOrderRequest
  }

  get isOverrideRequest() {
    return this._code === Ticket_Type_Code.CpOverrideRequest
  }

  get isNewMemberRequest() {
    return this._code === Ticket_Type_Code.CpNewMemberRequest
  }

  get isNavBenefitEducationRequest() {
    return this._code === Ticket_Type_Code.CpNavBenefitEducationRequest
  }

  get isNavCareRequest() {
    return this._code === Ticket_Type_Code.CpNavCareRequest
  }

  get isNavClinicalSupportRequest() {
    return this._code === Ticket_Type_Code.CpNavClinicalSupportRequest
  }

  get isNavMemberServicesRequest() {
    return this._code === Ticket_Type_Code.CpNavMemberServicesRequest
  }

  get isExistingNavBenefitEducationRequest() {
    return this._code === Ticket_Type_Code.CpExistingNavBenefitEducationRequest
  }

  get isExistingNavCareRequest() {
    return this._code === Ticket_Type_Code.CpExistingNavCareRequest
  }

  get isExistingNavClinicalSupportRequest() {
    return this._code === Ticket_Type_Code.CpExistingNavClinicalSupportRequest
  }

  get isExistingNavMemberServicesRequest() {
    return this._code === Ticket_Type_Code.CpExistingNavMemberServicesRequest
  }

  get isNavBillingAdvocacyRequest() {
    return this._code === 'cp_nav_billing_advocacy_request'
  }

  get isExistingNavBillingAdvocacyRequest() {
    return this._code === 'cp_existing_nav_billing_advocacy_request'
  }

  get isAnyExistingNavRequest() {
    return (
      this.isExistingNavBenefitEducationRequest ||
      this.isExistingNavCareRequest ||
      this.isExistingNavClinicalSupportRequest ||
      this.isExistingNavMemberServicesRequest ||
      this.isExistingNavBillingAdvocacyRequest
    )
  }

  get isBenefitsQuestion() {
    return this._code === Ticket_Type_Code.BenefitsQuestion
  }

  get isTriage() {
    return this._code === Ticket_Type_Code.Triage
  }

  get isTelemedicine() {
    return this._code === Ticket_Type_Code.TelemedicineScheduling
  }

  get isMedical() {
    return this._code === Ticket_Type_Code.MedicalNavigation
  }

  get isOutboundEngagement() {
    return this._code === Ticket_Type_Code.OutboundEngagement
  }

  get isPlanSelection() {
    return this._code === Ticket_Type_Code.PlanSelection
  }

  get isFollowUp() {
    return this._code === Ticket_Type_Code.FollowUp
  }

  get isClinicalFollowUp() {
    return this._code === Ticket_Type_Code.ClinicalFollowUp
  }

  get isAnnualPhysical() {
    return this._code === Ticket_Type_Code.Wellness
  }

  get isSeldon() {
    return this._code === Ticket_Type_Code.SeldonBeattieWellness
  }

  get isPbm() {
    return this._code.includes('pbm')
  }

  get isNavGeneralInquiry() {
    return this._code === Ticket_Type_Code.CpNavGeneralInquiry
  }

  get isNavOnsiteRequest() {
    return this._code === Ticket_Type_Code.CpNavOnsiteRequest
  }

  get isNavReportRequest() {
    return this._code === Ticket_Type_Code.CpNavReportRequest
  }

  get isNavOverviewDemoRequest() {
    return this._code === Ticket_Type_Code.CpNavOverviewDemoRequest
  }

  get isTaskManagerEnabled() {
    return this._group.isTaskManagerEnabled
  }

  get isVendorSetup() {
    return this._code === Ticket_Type_Code.PbmDoVendorSetup
  }

  get isNewImplementation() {
    return this._code === Ticket_Type_Code.PbmDoNewImplementation
  }

  get isGroupTerming() {
    return this._code === Ticket_Type_Code.PbmDoGroupTerming
  }

  get isClaimFileProcessing() {
    return this._code === Ticket_Type_Code.PbmDoClaimFileProcessing
  }

  get isImpactReport() {
    return this._code === Ticket_Type_Code.PbmDoImpactReport
  }

  get isAccumulatorChange() {
    return this._code === Ticket_Type_Code.PbmDoPchAccumulatorChange
  }

  get isFertilityMaximum() {
    return this._code === Ticket_Type_Code.PbmDoPchFertilityMaximum
  }

  get isGroupNumberChange() {
    return this._code === Ticket_Type_Code.PbmDoPchGroupNumberChange
  }

  get isPlanChangeHandling() {
    return this._code === Ticket_Type_Code.PbmDoPchOther
  }

  get isEligibilityInquiry() {
    return this._code === Ticket_Type_Code.PbmDoEligibilityInquiry
  }
}

export default TicketType
